import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Calculator, Info, Scale, Ruler } from 'lucide-react';

const Index = () => {
  const [weight, setWeight] = useState<string>('');
  const [height, setHeight] = useState<string>('');
  const [heightUnit, setHeightUnit] = useState<'cm' | 'm'>('cm');
  const [bmi, setBmi] = useState<number | null>(null);
  const [category, setCategory] = useState<string>('');
  const [categoryColor, setCategoryColor] = useState<string>('');
  const [error, setError] = useState<string>('');

  const getBMICategory = (bmiValue: number) => {
    if (bmiValue < 18.5) {
      return { category: 'Untergewicht', color: 'text-blue-600', bgColor: 'bg-blue-50 border-blue-200' };
    } else if (bmiValue >= 18.5 && bmiValue <= 24.9) {
      return { category: 'Normalgewicht', color: 'text-green-600', bgColor: 'bg-green-50 border-green-200' };
    } else if (bmiValue >= 25 && bmiValue <= 29.9) {
      return { category: 'Übergewicht', color: 'text-yellow-600', bgColor: 'bg-yellow-50 border-yellow-200' };
    } else {
      return { category: 'Adipositas', color: 'text-red-600', bgColor: 'bg-red-50 border-red-200' };
    }
  };

  const validateInputs = (weightValue: number, heightValue: number): string => {
    if (weightValue <= 0 || weightValue > 1000) {
      return 'Bitte geben Sie ein realistisches Gewicht ein (1-1000 kg).';
    }
    if (heightUnit === 'cm' && (heightValue <= 0 || heightValue > 300)) {
      return 'Bitte geben Sie eine realistische Körpergröße ein (1-300 cm).';
    }
    if (heightUnit === 'm' && (heightValue <= 0 || heightValue > 3)) {
      return 'Bitte geben Sie eine realistische Körpergröße ein (0.1-3.0 m).';
    }
    return '';
  };

  const calculateBMI = () => {
    const weightValue = parseFloat(weight);
    const heightValue = parseFloat(height);

    if (isNaN(weightValue) || isNaN(heightValue)) {
      setError('Bitte geben Sie gültige Zahlen ein.');
      return;
    }

    const validationError = validateInputs(weightValue, heightValue);
    if (validationError) {
      setError(validationError);
      return;
    }

    setError('');

    // Convert height to meters if necessary
    const heightInMeters = heightUnit === 'cm' ? heightValue / 100 : heightValue;
    
    // Calculate BMI
    const bmiValue = weightValue / (heightInMeters * heightInMeters);
    const roundedBMI = Math.round(bmiValue * 10) / 10;
    
    setBmi(roundedBMI);
    
    const categoryInfo = getBMICategory(roundedBMI);
    setCategory(categoryInfo.category);
    setCategoryColor(categoryInfo.color);
  };

  // Auto-calculate when inputs change
  useEffect(() => {
    if (weight && height) {
      calculateBMI();
    } else {
      setBmi(null);
      setCategory('');
      setError('');
    }
  }, [weight, height, heightUnit]);

  const handleWeightChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      setWeight(value);
    }
  };

  const handleHeightChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      setHeight(value);
    }
  };

  const getBMIResultCard = () => {
    if (!bmi) return null;
    
    const categoryInfo = getBMICategory(bmi);
    
    return (
      <Card className={`mt-6 border-2 ${categoryInfo.bgColor} shadow-[var(--shadow-result)] transition-all duration-300`}>
        <CardHeader className="text-center pb-3">
          <CardTitle className="text-2xl font-bold text-primary flex items-center justify-center gap-2">
            <Calculator className="h-6 w-6" />
            BMI-Ergebnis
          </CardTitle>
        </CardHeader>
        <CardContent className="text-center">
          <div className="text-4xl font-bold mb-2 text-primary">
            {bmi}
          </div>
          <div className={`text-xl font-semibold mb-4 ${categoryInfo.color}`}>
            {category}
          </div>
          
          {/* BMI Scale Visualization */}
          <div className="mt-6 p-4 bg-white/50 rounded-lg">
            <h4 className="font-semibold mb-3 text-sm text-muted-foreground">BMI-Kategorien:</h4>
            <div className="space-y-2 text-sm">
              <div className={`flex justify-between p-2 rounded ${bmi < 18.5 ? 'bg-blue-100 font-semibold' : ''}`}>
                <span>Untergewicht</span>
                <span className="text-blue-600">&lt; 18.5</span>
              </div>
              <div className={`flex justify-between p-2 rounded ${bmi >= 18.5 && bmi <= 24.9 ? 'bg-green-100 font-semibold' : ''}`}>
                <span>Normalgewicht</span>
                <span className="text-green-600">18.5 - 24.9</span>
              </div>
              <div className={`flex justify-between p-2 rounded ${bmi >= 25 && bmi <= 29.9 ? 'bg-yellow-100 font-semibold' : ''}`}>
                <span>Übergewicht</span>
                <span className="text-yellow-600">25.0 - 29.9</span>
              </div>
              <div className={`flex justify-between p-2 rounded ${bmi >= 30 ? 'bg-red-100 font-semibold' : ''}`}>
                <span>Adipositas</span>
                <span className="text-red-600">≥ 30.0</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/30 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="p-3 bg-primary/10 rounded-full">
              <Scale className="h-8 w-8 text-primary" />
            </div>
            <h1 className="text-4xl font-bold text-primary">BMI-Rechner</h1>
          </div>
          <p className="text-lg text-muted-foreground max-w-md mx-auto">
            Berechnen Sie Ihren Body-Mass-Index schnell und einfach
          </p>
        </div>

        {/* Main Calculator Card */}
        <Card className="shadow-[var(--shadow-elegant)] border-0 bg-white/80 backdrop-blur-sm">
          <CardHeader>
            <CardTitle className="text-2xl font-semibold text-center text-primary">
              Ihre Körperdaten
            </CardTitle>
            <CardDescription className="text-center">
              Geben Sie Ihr Gewicht und Ihre Körpergröße ein
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Weight Input */}
            <div className="space-y-2">
              <Label htmlFor="weight" className="text-base font-medium flex items-center gap-2">
                <Scale className="h-4 w-4" />
                Körpergewicht
              </Label>
              <div className="relative">
                <Input
                  id="weight"
                  type="text"
                  placeholder="z.B. 70"
                  value={weight}
                  onChange={handleWeightChange}
                  className="text-lg h-12 pr-12"
                />
                <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground font-medium">
                  kg
                </span>
              </div>
            </div>

            {/* Height Input */}
            <div className="space-y-2">
              <Label htmlFor="height" className="text-base font-medium flex items-center gap-2">
                <Ruler className="h-4 w-4" />
                Körpergröße
              </Label>
              <div className="flex gap-3">
                <div className="relative flex-1">
                  <Input
                    id="height"
                    type="text"
                    placeholder={heightUnit === 'cm' ? 'z.B. 175' : 'z.B. 1.75'}
                    value={height}
                    onChange={handleHeightChange}
                    className="text-lg h-12 pr-12"
                  />
                  <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground font-medium">
                    {heightUnit}
                  </span>
                </div>
                <Select value={heightUnit} onValueChange={(value: 'cm' | 'm') => {
                  setHeightUnit(value);
                  setHeight(''); // Clear height when unit changes
                }}>
                  <SelectTrigger className="w-20 h-12">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cm">cm</SelectItem>
                    <SelectItem value="m">m</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Manual Calculate Button (optional) */}
            <Button 
              onClick={calculateBMI} 
              className="w-full h-12 text-lg font-semibold bg-gradient-to-r from-primary to-primary-glow hover:shadow-lg transition-all duration-300"
              disabled={!weight || !height}
            >
              <Calculator className="mr-2 h-5 w-5" />
              BMI berechnen
            </Button>

            {/* Error Display */}
            {error && (
              <Alert className="border-red-200 bg-red-50">
                <Info className="h-4 w-4 text-red-600" />
                <AlertDescription className="text-red-700">
                  {error}
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>

        {/* BMI Result */}
        {getBMIResultCard()}

        {/* Medical Disclaimer */}
        <Card className="mt-6 bg-muted/30 border-muted">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <Info className="h-5 w-5 text-muted-foreground mt-0.5 flex-shrink-0" />
              <div className="text-sm text-muted-foreground leading-relaxed">
                <p className="font-medium mb-1">Medizinischer Hinweis:</p>
                <p>
                  Der Body-Mass-Index (BMI) ist ein Richtwert zur Bewertung des Körpergewichts 
                  und ersetzt keine individuelle medizinische Beurteilung. Faktoren wie Muskelmasse, 
                  Körperbau und Alter werden nicht berücksichtigt. Bei Fragen zu Ihrem Gewicht 
                  konsultieren Sie bitte einen Arzt oder Ernährungsberater.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Index;
